import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, Download, Trash2, Package, Loader2, FileArchive
} from 'lucide-react';

import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../ui/button';

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-DO', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (_err) {
    return iso;
  }
};

const DesktopReleasesTab = () => {
  const { getAuthHeaders } = useAuth();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/desktop-releases`, { headers: getAuthHeaders() });
      setReleases(data.releases || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar releases desktop'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleases();
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    if (selected && !selected.name.toLowerCase().endsWith('.zip')) {
      toast.error('Solo se permiten archivos .zip');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecciona primero el ZIP del desktop');
      return;
    }
    try {
      setUploading(true);
      setProgress(0);
      setUploadedBytes(0);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', version);
      fd.append('notes', notes);
      await axios.post(`${API}/desktop-releases/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          setUploadedBytes(e.loaded);
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        }
      });
      toast.success('Release publicado', { description: 'Ya puedes descargarlo desde cualquier máquina' });
      setFile(null);
      setVersion('');
      setNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchReleases();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo subir el release'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (release) => {
    // Descarga nativa del navegador: pedimos un token de vida corta y abrimos la
    // URL directa. Así el archivo se baja por streaming a disco (con la barra de
    // progreso del navegador) de inmediato, en vez de buferar el ZIP completo en
    // memoria con axios+blob, que era lo que hacía que tardara en "iniciar".
    try {
      setDownloadingId(release.id);
      const { data } = await axios.get(
        `${API}/desktop-releases/${release.id}/download-url`,
        { headers: getAuthHeaders() }
      );
      const url = `${API}/desktop-releases/${release.id}/download?token=${encodeURIComponent(data.token)}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = release.original_name || 'smartdine-desktop.zip';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo descargar el release'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (release) => {
    if (!window.confirm(`¿Eliminar el release "${release.original_name}"?`)) return;
    try {
      await axios.delete(`${API}/desktop-releases/${release.id}`, { headers: getAuthHeaders() });
      toast.success('Release eliminado');
      fetchReleases();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar el release'));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24, flexWrap: 'wrap', gap: 16
      }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#11181C', margin: 0 }}>
            Releases Desktop
          </h2>
          <p style={{ fontSize: 14, color: '#567482', margin: 0, marginTop: 4 }}>
            Sube el ZIP de SmartDine Desktop y descárgalo desde cualquier máquina
          </p>
        </div>
      </div>

      {/* Zona de subida */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24,
        border: '1px solid #E5EDEA', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#567482', marginBottom: 6 }}>
              Archivo ZIP del desktop
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileChange}
              style={{
                width: '100%', padding: 10, borderRadius: 10, border: '1px dashed #B7C4C0',
                background: '#F7FAF9', fontSize: 13, color: '#11181C'
              }}
            />
            {file && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#00796B', display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileArchive style={{ width: 14, height: 14 }} />
                {file.name} ({formatSize(file.size)})
              </div>
            )}
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#567482', marginBottom: 6 }}>
              Versión (opcional)
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="p.ej. 1.0.0"
              style={{
                width: '100%', padding: 10, borderRadius: 10, border: '1px solid #B7C4C0',
                fontSize: 14, color: '#11181C', boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#567482', marginBottom: 6 }}>
              Notas (opcional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Cambios incluidos en este build..."
              style={{
                width: '100%', padding: 10, borderRadius: 10, border: '1px solid #B7C4C0',
                fontSize: 14, color: '#11181C', boxSizing: 'border-box'
              }}
            />
          </div>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file}
            style={{
              background: '#00796B', color: '#fff', border: 'none', borderRadius: 12,
              padding: '11px 22px', fontWeight: 600, fontSize: 14,
              display: 'flex', alignItems: 'center', gap: 8, opacity: (uploading || !file) ? 0.6 : 1
            }}
          >
            {uploading
              ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
              : <UploadCloud style={{ width: 16, height: 16 }} />}
            {uploading ? `Subiendo... ${progress}%` : 'Publicar Release'}
          </Button>
        </div>
        {uploading && (
          <div style={{
            marginTop: 16, padding: 14, borderRadius: 12,
            background: '#F7FAF9', border: '1px solid #E5EDEA'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, fontSize: 13, color: '#11181C'
            }}>
              <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite', color: '#00796B' }} />
                Subiendo release...
              </span>
              <span style={{ color: '#567482' }}>
                {formatSize(uploadedBytes)} / {formatSize(file ? file.size : 0)}
                <span style={{ fontWeight: 700, color: '#00796B', marginLeft: 8 }}>{progress}%</span>
              </span>
            </div>
            <div style={{ height: 10, borderRadius: 5, background: '#E5EDEA', overflow: 'hidden' }}>
              <div style={{
                width: `${progress}%`, height: '100%', borderRadius: 5,
                background: 'linear-gradient(90deg, #00796B, #26A69A)',
                transition: 'width 0.25s ease'
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Listado de releases */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#567482', padding: 24 }}>
          <Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
          Cargando releases...
        </div>
      ) : releases.length === 0 ? (
        <div style={{
          background: '#fff', borderRadius: 16, padding: 40, textAlign: 'center',
          border: '1px solid #E5EDEA', color: '#567482'
        }}>
          <Package style={{ width: 40, height: 40, margin: '0 auto 12px', opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: 15 }}>Aún no hay releases desktop publicados</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {releases.map((release) => (
            <div
              key={release.id}
              style={{
                background: '#fff', borderRadius: 14, padding: '16px 20px',
                border: '1px solid #E5EDEA', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, background: '#FFF7ED',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <FileArchive style={{ width: 20, height: 20, color: '#EA580C' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 600, color: '#11181C',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {release.original_name}
                    {release.version && (
                      <span style={{
                        marginLeft: 8, fontSize: 12, fontWeight: 600, color: '#00796B',
                        background: '#E6F4F1', padding: '2px 8px', borderRadius: 8
                      }}>
                        v{release.version}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#567482', marginTop: 3 }}>
                    {formatSize(release.size_bytes)} · {formatDate(release.uploaded_at)} · por {release.uploaded_by || '-'}
                    {release.notes && (
                      <span style={{ display: 'block', marginTop: 2, fontStyle: 'italic' }}>{release.notes}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <Button
                  onClick={() => handleDownload(release)}
                  disabled={downloadingId === release.id}
                  title="Descargar ZIP"
                  style={{
                    background: '#00796B', color: '#fff', border: 'none', borderRadius: 10,
                    padding: '8px 14px', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                    opacity: downloadingId === release.id ? 0.6 : 1
                  }}
                >
                  {downloadingId === release.id
                    ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                    : <Download style={{ width: 14, height: 14 }} />}
                  {downloadingId === release.id ? 'Iniciando...' : 'Descargar'}
                </Button>
                <Button
                  onClick={() => handleDelete(release)}
                  title="Eliminar release"
                  style={{
                    background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA',
                    borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  <Trash2 style={{ width: 14, height: 14 }} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesktopReleasesTab;
